/**
 * TicketSidebarCards - Aside da página T1 (Layout Plano A — Task 9).
 *
 * Composição dos cards da sidebar direita, substituindo o
 * `TicketDetailSidebar` monolítico legado (deletado nesta task — lógica
 * movida pra cada card). Ordem: Descrição, Detalhes, Pessoas, Cliente &
 * Contato, SLA & Tempos, Projeto (condicional).
 */
import React from 'react';
import { DescriptionCard } from './DescriptionCard';
import { DetailsCard } from './DetailsCard';
import { PeopleCard } from './PeopleCard';
import type { SidebarUser, SidebarTeam } from './PeopleCard';
import { ClientContactCard } from './ClientContactCard';
import { SlaCard } from './SlaCard';
import { ProjectCard } from './ProjectCard';
import type {
  PopulatedTicket,
  TicketPriority,
  TicketStageResponse,
  UpdateTicketRequest,
} from '../../types';

export type { SidebarUser, SidebarTeam };

export interface TicketSidebarCardsProps {
  ticket: PopulatedTicket;
  stages: TicketStageResponse[];
  users: SidebarUser[];
  teams: SidebarTeam[];
  onUpdate: (data: Partial<UpdateTicketRequest>) => void;
  onStageChange: (stageId: string) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  onAssigneeChange: (assigneeId: string | null) => void;
  onTeamChange: (teamId: string | null) => void;
  /** Troca (ou `''` para limpar) o vínculo `ticket.customerId` (Plano C, Task 5). */
  onCustomerChange: (customerId: string) => void;
  /** Adiciona a tag informada (input inline no DetailsCard). */
  onAddTag?: (tag: string) => void;
  onRemoveTag?: (tag: string) => void;
  onStageChangeWithConfirmation: (stageId: string, stage: TicketStageResponse) => void;
  /** Renderizado dentro de um drawer de largura fixa — solta a coluna de 318px (Fix 1, 2026-08-11). */
  compact?: boolean;
  loading?: boolean;
}

export const TicketSidebarCards: React.FC<TicketSidebarCardsProps> = ({
  ticket,
  stages,
  users,
  teams,
  onUpdate,
  onStageChange,
  onPriorityChange,
  onAssigneeChange,
  onTeamChange,
  onCustomerChange,
  onAddTag,
  onRemoveTag,
  onStageChangeWithConfirmation,
  compact = false,
  loading = false,
}) => {
  return (
    <aside
      data-testid="ticket-detail-sidebar"
      /* mobile e compact (drawer): largura total — vira a aba "Detalhes".
         lg fora do compact (página de detalhe): coluna fixa 318 c/ divisor. */
      className={`flex-shrink-0 overflow-y-auto w-full ${compact ? '' : 'lg:w-[318px] lg:border-l'}`}
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: 'var(--theme-page-background)',
      }}
    >
      <div className="p-3.5 flex flex-col gap-3">
        <DescriptionCard ticket={ticket} onUpdate={onUpdate} loading={loading} />

        <DetailsCard
          ticket={ticket}
          stages={stages}
          onStageChange={onStageChange}
          onPriorityChange={onPriorityChange}
          onStageChangeWithConfirmation={onStageChangeWithConfirmation}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          loading={loading}
        />

        <PeopleCard
          ticket={ticket}
          users={users}
          teams={teams}
          onAssigneeChange={onAssigneeChange}
          onTeamChange={onTeamChange}
          loading={loading}
        />

        <ClientContactCard ticket={ticket} onCustomerChange={onCustomerChange} loading={loading} />

        <SlaCard ticket={ticket} />

        <ProjectCard ticket={ticket} />
      </div>
    </aside>
  );
};

TicketSidebarCards.displayName = 'TicketSidebarCards';
